import React, { useState, useEffect } from 'react';

interface AIImageGeneratorProps {
    onGenerate: (imageUrl: string, prompt: string) => void;
    onClose: () => void;
    theme?: 'light' | 'dark';
    language?: 'en' | 'sv';
}

interface GalleryImage {
    filename: string;
    url: string;
    prompt: string;
    generatedAt: string;
    size: number;
}

const translations = {
    en: {
        title: 'AI Image Generator',
        subtitle: 'Create images with Gemini Imagen AI',
        promptLabel: 'Describe the image you want to create',
        promptPlaceholder: 'A beautiful sunset over the Mediterranean sea with a luxury yacht...',
        style: 'Style',
        size: 'Size',
        generate: 'Generate Image',
        generating: 'Creating with AI...',
        useImage: 'Use This Image',
        regenerate: 'Regenerate',
        close: 'Close',
        gallery: 'Previously Generated',
        noGallery: 'No previously generated images',
        loadingGallery: 'Loading gallery...',
        deleteConfirm: 'Delete this image?',
        deleted: 'Image deleted',
        error: 'Generation failed',
        styles: {
            realistic: 'Realistic',
            artistic: 'Artistic',
            cartoon: 'Cartoon',
            minimalist: 'Minimalist',
            vintage: 'Vintage',
            futuristic: 'Futuristic'
        },
        sizes: {
            square: 'Square (1:1)',
            landscape: 'Landscape (16:9)',
            portrait: 'Portrait (9:16)',
            wide: 'Ultra Wide (21:9)'
        },
        tips: 'Tips for better results:',
        tip1: 'Be specific about colors, lighting, and mood',
        tip2: 'Mention the style you want (photo, illustration, etc.)',
        tip3: 'Include details about composition and perspective',
        poweredBy: 'Powered by Gemini Imagen 4.0'
    },
    sv: {
        title: 'AI Bildgenerator',
        subtitle: 'Skapa bilder med Gemini Imagen AI',
        promptLabel: 'Beskriv bilden du vill skapa',
        promptPlaceholder: 'En vacker solnedgång över Medelhavet med en lyxig yacht...',
        style: 'Stil',
        size: 'Storlek',
        generate: 'Generera Bild',
        generating: 'Skapar med AI...',
        useImage: 'Använd Denna Bild',
        regenerate: 'Generera Igen',
        close: 'Stäng',
        gallery: 'Tidigare Genererade',
        noGallery: 'Inga tidigare genererade bilder',
        loadingGallery: 'Laddar galleri...',
        deleteConfirm: 'Ta bort denna bild?',
        deleted: 'Bild borttagen',
        error: 'Generering misslyckades',
        styles: {
            realistic: 'Realistisk',
            artistic: 'Konstnärlig',
            cartoon: 'Tecknad',
            minimalist: 'Minimalistisk',
            vintage: 'Vintage',
            futuristic: 'Futuristisk'
        },
        sizes: {
            square: 'Kvadrat (1:1)',
            landscape: 'Liggande (16:9)',
            portrait: 'Stående (9:16)',
            wide: 'Ultrabredd (21:9)'
        },
        tips: 'Tips för bättre resultat:',
        tip1: 'Var specifik om färger, ljus och stämning',
        tip2: 'Nämn stilen du vill ha (foto, illustration, etc.)',
        tip3: 'Inkludera detaljer om komposition och perspektiv',
        poweredBy: 'Drivs av Gemini Imagen 4.0'
    }
};

// Map size names to pixel dimensions
const sizeDimensions: Record<string, { width: number; height: number }> = {
    square: { width: 1024, height: 1024 },
    landscape: { width: 1536, height: 1024 },
    portrait: { width: 1024, height: 1536 },
    wide: { width: 1792, height: 768 }
};

const AIImageGenerator: React.FC<AIImageGeneratorProps> = ({
    onGenerate,
    onClose,
    theme = 'dark',
    language = 'en'
}) => {
    const isDark = theme === 'dark';
    const t = translations[language] || translations.en;

    const [prompt, setPrompt] = useState('');
    const [style, setStyle] = useState('realistic');
    const [size, setSize] = useState('landscape');
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedImage, setGeneratedImage] = useState<{ url: string; filename: string } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'generate' | 'gallery'>('generate');
    const [gallery, setGallery] = useState<GalleryImage[]>([]);
    const [loadingGallery, setLoadingGallery] = useState(false);

    // Load gallery on mount
    useEffect(() => {
        loadGallery();
    }, []);

    const loadGallery = async () => {
        setLoadingGallery(true);
        try {
            const response = await fetch('/api/media/ai/gallery');
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setGallery(data.images || []);
                }
            }
        } catch (err) {
            console.error('Failed to load AI gallery:', err);
        } finally {
            setLoadingGallery(false);
        }
    };

    const generateImage = async () => {
        if (!prompt.trim()) return;

        setIsGenerating(true);
        setError(null);
        setGeneratedImage(null);

        try {
            const dims = sizeDimensions[size] || sizeDimensions.landscape;

            // Build the full prompt with style modifier
            const styleModifiers: Record<string, string> = {
                realistic: 'photorealistic, high quality photography',
                artistic: 'artistic painting style, fine art',
                cartoon: 'cartoon illustration style, vibrant colors',
                minimalist: 'minimalist, clean, simple composition',
                vintage: 'vintage retro style, film grain, warm tones',
                futuristic: 'futuristic sci-fi style, neon, cyberpunk'
            };
            const fullPrompt = `${prompt}. ${styleModifiers[style] || ''}`.trim();

            const response = await fetch('/api/media/ai/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prompt: fullPrompt,
                    width: dims.width,
                    height: dims.height,
                    num_images: 1
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || `Server error: ${response.status}`);
            }

            if (data.success && data.data?.images?.length > 0) {
                const img = data.data.images[0];
                setGeneratedImage({
                    url: img.url,
                    filename: img.filename
                });
                // Refresh gallery
                loadGallery();
            } else {
                throw new Error(data.error || 'No image was generated');
            }
        } catch (err: any) {
            console.error('AI Generation error:', err);
            setError(err.message || t.error);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleUseImage = () => {
        if (generatedImage) {
            onGenerate(generatedImage.url, prompt);
        }
    };

    const handleUseGalleryImage = (image: GalleryImage) => {
        onGenerate(image.url, image.prompt);
    };

    const handleDeleteGalleryImage = async (filename: string) => {
        if (!confirm(t.deleteConfirm)) return;
        try {
            const response = await fetch(`/api/media/ai/gallery/${filename}`, { method: 'DELETE' });
            if (response.ok) {
                setGallery(prev => prev.filter(img => img.filename !== filename));
            }
        } catch (err) {
            console.error('Failed to delete AI image:', err);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className={`w-full max-w-5xl max-h-[90vh] rounded-2xl overflow-hidden flex flex-col ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
                {/* Header */}
                <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                    <div>
                        <h2 className="text-xl font-bold flex items-center gap-2">
                            <span className="material-icons text-purple-500">auto_awesome</span>
                            {t.title}
                        </h2>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{t.subtitle}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Tab Switcher */}
                        <div className={`flex rounded-lg p-0.5 ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
                            <button
                                onClick={() => setActiveTab('generate')}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'generate'
                                        ? 'bg-purple-600 text-white shadow'
                                        : isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'
                                    }`}
                            >
                                <span className="material-icons text-sm mr-1" style={{ verticalAlign: 'middle' }}>auto_awesome</span>
                                {t.generate.split(' ')[0]}
                            </button>
                            <button
                                onClick={() => setActiveTab('gallery')}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'gallery'
                                        ? 'bg-purple-600 text-white shadow'
                                        : isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'
                                    }`}
                            >
                                <span className="material-icons text-sm mr-1" style={{ verticalAlign: 'middle' }}>photo_library</span>
                                {t.gallery}
                                {gallery.length > 0 && (
                                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                                        {gallery.length}
                                    </span>
                                )}
                            </button>
                        </div>
                        <button
                            onClick={onClose}
                            className={`p-2 rounded-lg ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                        >
                            <span className="material-icons">close</span>
                        </button>
                    </div>
                </div>

                {activeTab === 'generate' ? (
                    <div className="flex-1 flex overflow-hidden">
                        {/* Left: Controls */}
                        <div className={`w-80 flex-shrink-0 p-6 border-r overflow-y-auto ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                            {/* Prompt Input */}
                            <div className="mb-5">
                                <label className="block text-sm font-medium mb-2">{t.promptLabel}</label>
                                <textarea
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    placeholder={t.promptPlaceholder}
                                    rows={4}
                                    className={`w-full px-4 py-3 rounded-xl resize-none outline-none transition-colors ${isDark
                                        ? 'bg-slate-800 border border-slate-700 focus:border-purple-500'
                                        : 'bg-gray-50 border border-gray-200 focus:border-purple-500'
                                        }`}
                                />
                            </div>

                            {/* Style Selection */}
                            <div className="mb-5">
                                <label className="block text-sm font-medium mb-2">{t.style}</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {Object.entries(t.styles).map(([key, label]) => (
                                        <button
                                            key={key}
                                            onClick={() => setStyle(key)}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${style === key
                                                ? 'bg-purple-600 text-white shadow-md'
                                                : isDark ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'
                                                }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Size Selection */}
                            <div className="mb-5">
                                <label className="block text-sm font-medium mb-2">{t.size}</label>
                                <div className="space-y-2">
                                    {Object.entries(t.sizes).map(([key, label]) => {
                                        const dims = sizeDimensions[key];
                                        return (
                                            <button
                                                key={key}
                                                onClick={() => setSize(key)}
                                                className={`w-full px-4 py-2.5 rounded-lg text-sm font-medium text-left transition-all flex items-center justify-between ${size === key
                                                    ? 'bg-purple-600 text-white shadow-md'
                                                    : isDark ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'
                                                    }`}
                                            >
                                                <span>{label}</span>
                                                {dims && (
                                                    <span className={`text-xs ${size === key ? 'text-purple-200' : isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                                                        {dims.width}×{dims.height}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Generate Button */}
                            <button
                                onClick={generateImage}
                                disabled={!prompt.trim() || isGenerating}
                                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                            >
                                {isGenerating ? (
                                    <>
                                        <span className="material-icons animate-spin">sync</span>
                                        {t.generating}
                                    </>
                                ) : (
                                    <>
                                        <span className="material-icons">auto_awesome</span>
                                        {t.generate}
                                    </>
                                )}
                            </button>

                            {/* Tips */}
                            <div className={`mt-5 p-4 rounded-xl ${isDark ? 'bg-slate-800/50' : 'bg-gray-50'}`}>
                                <p className="text-sm font-medium mb-2">{t.tips}</p>
                                <ul className={`text-xs space-y-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                    <li>• {t.tip1}</li>
                                    <li>• {t.tip2}</li>
                                    <li>• {t.tip3}</li>
                                </ul>
                            </div>

                            {/* Powered By */}
                            <div className={`mt-3 text-center text-xs ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
                                <span className="material-icons text-xs mr-1" style={{ verticalAlign: 'middle' }}>smart_toy</span>
                                {t.poweredBy}
                            </div>
                        </div>

                        {/* Right: Preview */}
                        <div className={`flex-1 flex flex-col items-center justify-center p-6 ${isDark ? 'bg-slate-950/50' : 'bg-gray-50'}`}>
                            {isGenerating ? (
                                <div className="text-center">
                                    <div className="relative w-24 h-24 mx-auto mb-6">
                                        <div className="absolute inset-0 border-4 border-purple-500/30 rounded-full"></div>
                                        <div className="absolute inset-0 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                                        <div className="absolute inset-3 border-4 border-pink-500/30 rounded-full"></div>
                                        <div className="absolute inset-3 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                                        <span className="material-icons text-purple-400 absolute inset-0 flex items-center justify-center text-2xl">auto_awesome</span>
                                    </div>
                                    <p className={`text-lg font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{t.generating}</p>
                                    <p className={`text-sm mt-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                                        {language === 'sv' ? 'Detta kan ta 10-30 sekunder...' : 'This may take 10-30 seconds...'}
                                    </p>
                                </div>
                            ) : generatedImage ? (
                                <div className="text-center w-full">
                                    <img
                                        src={generatedImage.url}
                                        alt="AI Generated"
                                        className="max-w-full max-h-[50vh] rounded-xl shadow-2xl mb-6 mx-auto"
                                    />
                                    <div className="flex gap-3 justify-center">
                                        <button
                                            onClick={generateImage}
                                            className={`px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-colors ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                                        >
                                            <span className="material-icons text-sm">refresh</span>
                                            {t.regenerate}
                                        </button>
                                        <button
                                            onClick={handleUseImage}
                                            className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-xl flex items-center gap-2 hover:shadow-lg transition-all"
                                        >
                                            <span className="material-icons text-sm">check</span>
                                            {t.useImage}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <div className={`w-32 h-32 rounded-2xl flex items-center justify-center mb-4 mx-auto ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
                                        <span className="material-icons text-6xl text-purple-500/50">auto_awesome</span>
                                    </div>
                                    <p className={isDark ? 'text-slate-500' : 'text-gray-400'}>
                                        {language === 'sv' ? 'Din genererade bild visas här' : 'Your generated image will appear here'}
                                    </p>
                                </div>
                            )}

                            {error && (
                                <div className="mt-4 px-4 py-3 bg-red-500/20 border border-red-500/30 text-red-400 rounded-xl text-sm flex items-start gap-2 max-w-md">
                                    <span className="material-icons text-sm mt-0.5">error</span>
                                    <div>
                                        <p className="font-medium">{t.error}</p>
                                        <p className="text-xs mt-1 opacity-80">{error}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    /* Gallery Tab */
                    <div className="flex-1 overflow-y-auto p-6">
                        {loadingGallery ? (
                            <div className="flex items-center justify-center h-40">
                                <div className="w-8 h-8 border-3 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                                <span className={`ml-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{t.loadingGallery}</span>
                            </div>
                        ) : gallery.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-40">
                                <span className={`material-icons text-4xl mb-2 ${isDark ? 'text-slate-600' : 'text-gray-300'}`}>photo_library</span>
                                <p className={isDark ? 'text-slate-500' : 'text-gray-400'}>{t.noGallery}</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {gallery.map((image) => (
                                    <div
                                        key={image.filename}
                                        className={`group relative rounded-xl overflow-hidden border transition-all hover:shadow-lg ${isDark ? 'border-slate-700 hover:border-purple-500' : 'border-gray-200 hover:border-purple-400'}`}
                                    >
                                        <img
                                            src={image.url}
                                            alt={image.prompt}
                                            className="w-full aspect-square object-cover"
                                            loading="lazy"
                                        />
                                        {/* Hover Overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                                            <p className="text-white text-xs line-clamp-2 mb-2">{image.prompt}</p>
                                            <div className="flex items-center justify-between">
                                                <span className="text-white/60 text-xs">{formatFileSize(image.size)}</span>
                                                <div className="flex gap-1.5">
                                                    <button
                                                        onClick={() => handleDeleteGalleryImage(image.filename)}
                                                        className="p-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded-lg transition-colors"
                                                        title="Delete"
                                                    >
                                                        <span className="material-icons text-sm">delete</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleUseGalleryImage(image)}
                                                        className="p-1.5 bg-green-500/80 hover:bg-green-500 text-white rounded-lg transition-colors"
                                                        title={t.useImage}
                                                    >
                                                        <span className="material-icons text-sm">check</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AIImageGenerator;
